import { useEffect } from 'react'

import { Provider } from 'react-redux'
import { ToastContainer, toast } from 'react-toastify'

import { Web3Provider } from '@ethersproject/providers'
import { Web3ReactProvider } from '@web3-react/core'
import { PersistGate } from 'redux-persist/integration/react'
import { usePrivy } from '@privy-io/react-auth'

import { useApp } from 'store/app/hooks'
import { useAuth } from 'store/auth/hooks'
import { persistor, store } from 'store/store'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { TokenGatingProvider } from 'contexts/TokenGatingContext'
import { useAnticheatSync } from 'hooks/useAnticheatSync'


import 'react-toastify/dist/ReactToastify.css'

import Router from './router'

const AppRoot = () => {
  const { getInitialData } = useApp()
  const { ready, authenticated, user: privyUser } = usePrivy()
  const { user: reduxUser, signOut, refreshCurrentUser } = useAuth()

  // Initialize app-level anticheat sync (connects Socket.IO on app load)
  const { statuses: anticheatStatuses, isConnected: anticheatConnected, connectionError: anticheatError } = useAnticheatSync()

  // Store anticheat statuses in window for access by any component
  // This is a simple global store - can be replaced with Context/Redux if needed
  useEffect(() => {
    (window as any).__anticheatStatuses = anticheatStatuses
    ;(window as any).__anticheatConnected = anticheatConnected
    ;(window as any).__anticheatError = anticheatError
  }, [anticheatStatuses, anticheatConnected, anticheatError])

  // CRITICAL: Clean up old JWT tokens on app initialization (runs ONCE)
  useEffect(() => {
    const oldToken = localStorage.getItem('token') || sessionStorage.getItem('token')
    if (oldToken) {
      console.warn('🧹 REMOVING OLD HS256 TOKEN FROM STORAGE ON APP INIT')
      localStorage.removeItem('token')
      sessionStorage.removeItem('token')
    }

    // Also clean up any persisted Redux auth state with old tokens
    const persistedAuth = localStorage.getItem('persist:auth')
    if (persistedAuth) {
      try {
        const authState = JSON.parse(persistedAuth)
        if (authState && (authState.token || (authState.user && authState.user !== 'null'))) {
          const userObj = authState.user && authState.user !== 'null' ? JSON.parse(authState.user) : null
          if (authState.token || (userObj && userObj.token)) {
            console.warn('🧹 REMOVING OLD TOKEN FROM PERSISTED REDUX STATE')
            if (authState.token) delete authState.token
            if (userObj && userObj.token) {
              delete userObj.token
              authState.user = JSON.stringify(userObj)
            }
            localStorage.setItem('persist:auth', JSON.stringify(authState))
          }
        }
      } catch (e) {
        console.warn('Could not parse persisted auth state:', e)
      }
    }
  }, []) // Empty dependency array = runs ONCE on mount

  useEffect(() => {
    getInitialData()
  }, [getInitialData])

  // Monitor Privy session state and detect session loss
  useEffect(() => {
    if (ready) {
      console.log('🔐 Privy Session State:', {
        ready,
        authenticated,
        hasPrivyUser: !!privyUser,
        privyUserId: privyUser?.id,
        hasReduxUser: !!reduxUser,
        reduxUserId: reduxUser?._id,
        browser: navigator.userAgent.includes('Edg') ? 'Edge' : 'Other'
      })

      // CRITICAL: Detect session mismatch - Privy session lost but Redux still has user
      // This happens in Edge browser when cookies are cleared/expired
      if (!authenticated && reduxUser?._id) {
        console.error('🚨 SESSION MISMATCH DETECTED!')
        console.error('   Privy: authenticated = false')
        console.error('   Redux: user exists with ID:', reduxUser._id)
        console.error('   This will cause 401 errors on all API requests!')
        console.error('   Clearing Redux auth state and showing login prompt...')

        // Preserve tournament URL if user is on tournament page
        const currentPath = window.location.pathname
        if (currentPath.startsWith('/tournament/')) {
          const existingReturnUrl = localStorage.getItem('privy_return_url') || sessionStorage.getItem('privy_return_url')
          if (!existingReturnUrl || existingReturnUrl !== currentPath) {
            console.log('💾 Preserving tournament URL for re-login:', currentPath)
            localStorage.setItem('privy_return_url', currentPath)
            sessionStorage.setItem('privy_return_url', currentPath)
            localStorage.setItem('login_from_tournament', 'true')
            sessionStorage.setItem('login_from_tournament', 'true')
          }
        }

        // Clear Redux auth state to match Privy session
        signOut()

        // Show user-friendly message
        toast.error('Your session has expired. Please log in again to continue.', {
          position: 'top-center',
          autoClose: 5000,
        })

        // Clear the tracking flag
        sessionStorage.removeItem('was_authenticated')

        return
      }

      // Detect session loss (was authenticated before, now not)
      if (!authenticated && sessionStorage.getItem('was_authenticated')) {
        console.warn('⚠️ SESSION LOST')
        console.warn('⚠️ User was authenticated but session expired')
        console.warn('⚠️ This can happen when:')
        console.warn('   - Navigating from external links (email, other sites)')
        console.warn('   - Browser strict cookie policies (Edge, Firefox)')
        console.warn('   - Privy cookies expired or deleted')
        console.warn('⚠️ Privy SDK version:', '3.4.1')
        console.warn('⚠️ Cookie policy: Check Privy Dashboard SameSite setting')

        // Clear the flag to avoid repeated warnings
        sessionStorage.removeItem('was_authenticated')
      }

      // Track authentication state
      if (authenticated) {
        sessionStorage.setItem('was_authenticated', 'true')
      }
    }
  }, [ready, authenticated, privyUser, reduxUser, signOut])

  // CRITICAL: Auto-sync Redux when Privy session exists but Redux user is empty.
  // This handles cross-subdomain navigation where HttpOnly cookie carries the
  // Privy session but Redux/localStorage is empty (different per subdomain).
  useEffect(() => {
    if (ready && authenticated && !reduxUser) {
      console.log('🔄 Privy authenticated but Redux user is empty — syncing from /me')
      refreshCurrentUser()
    }
  }, [ready, authenticated, reduxUser, refreshCurrentUser])

  return <Router />
}

function App() {
  const getLibrary = (provider: any) => {
    const library = new Web3Provider(provider, 'any')
    library.pollingInterval = 15000
    return library
  }

  const queryClient = new QueryClient()

  // Clean up old authentication tokens on app load
  useEffect(() => {
    // Clear any old HS256 tokens from all storage locations
    const storageKeys = ['token', 'auth', 'persist:auth']
    storageKeys.forEach(key => {
      const stored = localStorage.getItem(key)
      if (stored) {
        try {
          // Check if it contains an HS256 token
          if (stored.includes('HS256') || stored.includes('eyJhbGciOiJIUzI1NiI')) {
            console.warn(`🧹 Clearing old auth data from localStorage: ${key}`)
            localStorage.removeItem(key)
          }
        } catch (e) {
          // Ignore parsing errors
        }
      }
    })
  }, [])

  return (
    <Web3ReactProvider getLibrary={getLibrary}>
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <TokenGatingProvider>
              <AppRoot />
              <ToastContainer theme="dark" />
            </TokenGatingProvider>
          </PersistGate>
        </Provider>
      </QueryClientProvider>
    </Web3ReactProvider>
  )
}

export default App
